/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_rec_arr, t_arr, t_rec, t_key } from '../types';
import filter from './filter';

type t_until_fn<V> = (v: V, k: t_key, adt: t_rec_arr<V>) => boolean;

export interface Until {
  /**
   * Mine values from an adt until a guard is met, when a condition is false.
   * The guard is inclusive.
   *
   * @public
   * @function f1.until
   * @param adt the array or record we mine values from until a guard is met
   * @example
   *
   * ```ts
   * f1.until((v) => v !== 17, [16, 17, 18]) === [16, 17]
   * ```
   *
   */
  <V>(fn: t_until_fn<V>, adt: t_arr<V>): t_arr<V>;
  <V>(fn: t_until_fn<V>, adt: t_rec<string, V>): t_rec<string, V>;
  <V>(fn: t_until_fn<V>, adt: t_rec_arr<V>): t_rec_arr<V>;

  <V>(fn: t_until_fn<V>): (adt: t_arr<V>) => t_arr<V>;
  <V>(fn: t_until_fn<V>): (adt: t_rec<string, V>) => t_rec<string, V>;
  <V>(fn: t_until_fn<V>): (adt: t_rec_arr<V>) => t_rec_arr<V>;
}

/**
 * @see Until
 */
export default <V>(fn: t_until_fn<V>, adt: t_rec_arr<V>): t_rec_arr<V> => {
  return filter((v, k, adt, stop) => {
    const guard = !fn(v, k, adt);
    if (guard) {
      stop();
    }

    return guard;
  }, adt);
};
